import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Environment, EnvironmentService, SessionService } from '@galaxy/core';

export const WebRTCCallState = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
} as const;

export type WebRTCCallState = (typeof WebRTCCallState)[keyof typeof WebRTCCallState];

export interface WebRTCCallError {
  message: string;
  code?: string;
  details?: unknown;
}

interface WebRTCDebugInfo {
  callState: WebRTCCallState;
  sessionId: string | null;
  peerConnectionState: RTCPeerConnectionState | undefined;
  iceConnectionState: RTCIceConnectionState | undefined;
  iceGatheringState: RTCIceGatheringState | undefined;
  signalingState: RTCSignalingState | undefined;
  hasLocalStream: boolean;
  hasRemoteStream: boolean;
  dataChannelState: RTCDataChannelState | undefined;
  audioQueueLength: number;
  isPlayingAudio: boolean;
  audioContextState: AudioContextState | undefined;
  localTracks: { kind: string; enabled: boolean; readyState: string }[] | undefined;
  remoteTracks: { kind: string; enabled: boolean; readyState: string }[] | undefined;
  timestamp: number;
}

/**
 * WebRTCCallService handles voice communication with the voice backend using WebRTC.
 *
 * This service uses WebRTC for media transport and HTTP POST for signaling,
 * bypassing WebSocket infrastructure issues (HTTP/2 vs HTTP/1.1 routing).
 *
 * Key specifications:
 * - Audio: Opus codec via WebRTC (browser handles encoding/decoding)
 * - Signaling: HTTP POST endpoints (not WebSocket)
 * - Sample rate: 48kHz (WebRTC default), resampled to 24kHz on backend for OpenAI
 */
@Injectable({
  providedIn: 'root',
})
export class WebRTCCallService implements OnDestroy {
  private static readonly LOCAL_URL = 'http://localhost:11001';
  private static readonly DEMO_URL = 'https://voice-demo.apigateway.co';
  private static readonly PROD_URL = 'https://voice-prod.apigateway.co';

  // Matches Deepgram TTS output sample rate
  private static readonly PLAYBACK_SAMPLE_RATE = 24000;
  // Small delay before first chunk plays, allowing subsequent chunks to be scheduled seamlessly
  private static readonly PLAYBACK_START_DELAY = 0.05;
  // Tolerance (in seconds) for detecting when the last scheduled chunk has finished playing
  private static readonly PLAYBACK_END_THRESHOLD = 0.01;
  private static readonly ICE_GATHERING_TIMEOUT_MS = 5000;
  // Debounce window — send ICE candidates in batches instead of one-by-one
  private static readonly ICE_CANDIDATE_BATCH_DELAY_MS = 100;
  // Number of audio chunks to buffer before starting playback to avoid stuttering
  private static readonly BUFFER_THRESHOLD = 3;

  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private sessionId: string | null = null;

  private audioDataChannel: RTCDataChannel | null = null;
  private audioContext: AudioContext | null = null;
  private audioQueue: Float32Array[] = [];
  private isPlayingAudio = false;
  private nextPlayTime = 0;

  private pendingIceCandidates: RTCIceCandidate[] = [];
  private iceCandidateSendTimer: ReturnType<typeof setTimeout> | undefined;

  public readonly callState = signal<WebRTCCallState>('idle');
  public readonly error = signal<WebRTCCallError | null>(null);
  public readonly remoteAudio = signal<MediaStream | null>(null);

  private readonly accountGroupId = signal<string | null>(null);
  private readonly partnerId = signal<string | null>(null);

  private readonly environmentService = inject(EnvironmentService);
  private readonly iamSessionId = inject(SessionService).getSessionIdSignal();

  private get baseUrl(): string {
    const env = this.environmentService.getEnvironment();
    if (env === Environment.LOCAL) return WebRTCCallService.LOCAL_URL;
    if (env === Environment.DEMO || env === Environment.TEST) return WebRTCCallService.DEMO_URL;
    return WebRTCCallService.PROD_URL;
  }

  setNamespaces(agid: string | null, pid: string | null) {
    this.accountGroupId.set(agid);
    this.partnerId.set(pid);
  }

  async startCall(assistantId: string): Promise<void> {
    try {
      this.setCallState(WebRTCCallState.CONNECTING);

      if (!this.iamSessionId()) {
        throw new Error('No active session — cannot authenticate WebRTC request');
      }

      this.sessionId = this.generateSessionId();
      await this.getUserMedia();
      this.createPeerConnection();
      this.addLocalTracks();
      await this.createAndSendOffer(assistantId);

      this.setCallState(WebRTCCallState.CONNECTED);
    } catch (error) {
      this.handleError('Failed to start WebRTC call', error);
    }
  }

  async endCall(): Promise<void> {
    try {
      if (this.sessionId) {
        await this.closeSession();
      }

      this.cleanup();
      this.setCallState(WebRTCCallState.DISCONNECTED);
    } catch (error) {
      this.handleError('Failed to end call', error);
    }
  }

  private async getUserMedia(): Promise<void> {
    const constraints: MediaStreamConstraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: { ideal: 48000 },
        channelCount: 1,
      },
    };

    this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
  }

  private createPeerConnection(): void {
    const config: RTCConfiguration = {
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }],
    };

    this.peerConnection = new RTCPeerConnection(config);

    this.peerConnection.onicecandidate = (event) => this.handleIceCandidate(event);
    this.peerConnection.oniceconnectionstatechange = () => this.handleIceConnectionStateChange();
    this.peerConnection.onconnectionstatechange = () => this.handleConnectionStateChange();
    this.peerConnection.ontrack = (event) => this.handleTrack(event);
    this.peerConnection.ondatachannel = (event) => this.handleDataChannel(event);

    // Required for the server to be able to create its own DataChannel —
    // ensures SCTP transport is negotiated in the SDP
    this.peerConnection.createDataChannel('negotiation-trigger', { negotiated: false });

    this.audioContext = new AudioContext();
  }

  private handleDataChannel(event: RTCDataChannelEvent): void {
    if (event.channel.label !== 'audio-out') return;

    this.audioDataChannel = event.channel;
    this.audioDataChannel.binaryType = 'arraybuffer';

    this.audioDataChannel.onmessage = (msgEvent) => {
      this.handleAudioData(msgEvent.data);
    };

    this.audioDataChannel.onerror = (error) => {
      this.handleError('Audio DataChannel error', error);
    };
  }

  private handleAudioData(data: ArrayBuffer): void {
    if (!this.audioContext) return;

    const int16Array = new Int16Array(data);
    const float32Array = new Float32Array(int16Array.length);

    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768.0;
    }

    this.audioQueue.push(float32Array);

    if (!this.isPlayingAudio && this.audioQueue.length >= WebRTCCallService.BUFFER_THRESHOLD) {
      this.startContinuousPlayback();
    } else if (this.isPlayingAudio) {
      this.scheduleQueuedChunks();
    }
  }

  private startContinuousPlayback(): void {
    if (!this.audioContext || this.audioQueue.length === 0) {
      return;
    }

    this.isPlayingAudio = true;
    this.nextPlayTime = this.audioContext.currentTime + WebRTCCallService.PLAYBACK_START_DELAY;

    this.scheduleQueuedChunks();
  }

  private scheduleQueuedChunks(): void {
    if (!this.audioContext) return;

    while (this.audioQueue.length > 0) {
      const float32Array = this.audioQueue.shift()!;

      try {
        const audioBuffer = this.audioContext.createBuffer(
          1,
          float32Array.length,
          WebRTCCallService.PLAYBACK_SAMPLE_RATE,
        );
        audioBuffer.getChannelData(0).set(float32Array);

        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.audioContext.destination);

        const startTime = Math.max(this.nextPlayTime, this.audioContext.currentTime);
        source.start(startTime);

        this.nextPlayTime = startTime + audioBuffer.duration;

        source.onended = () => {
          if (
            this.audioQueue.length === 0 &&
            this.audioContext &&
            this.audioContext.currentTime >= this.nextPlayTime - WebRTCCallService.PLAYBACK_END_THRESHOLD
          ) {
            this.isPlayingAudio = false;
          }
        };
      } catch (error) {
        this.handleError('Error scheduling audio chunk', error);
      }
    }
  }

  private addLocalTracks(): void {
    if (!this.localStream || !this.peerConnection) {
      throw new Error('Local stream or peer connection not initialized');
    }

    this.localStream.getTracks().forEach((track) => {
      this.peerConnection!.addTrack(track, this.localStream!);
    });
  }

  private async createAndSendOffer(assistantId: string): Promise<void> {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialized');
    }

    const offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });

    await this.peerConnection.setLocalDescription(offer);
    await this.waitForIceGathering();

    const finalOffer = this.peerConnection.localDescription;
    if (!finalOffer) {
      throw new Error('No local description available');
    }

    const agid = this.accountGroupId();
    const pid = this.partnerId();
    const namespaceId = assistantId === 'ASSISTANT-system' ? 'SYSTEM' : agid || pid;
    const namespaceType = assistantId === 'ASSISTANT-system' ? 'System' : agid ? 'AccountGroup' : 'Partner';

    const response = await fetch(`${this.baseUrl}/webrtc/offer`, {
      method: 'POST',
      headers: this.authHeaders,
      body: JSON.stringify({
        session_id: this.sessionId,
        namespace_id: namespaceId,
        namespace_type: namespaceType,
        assistant_id: assistantId,
        sdp: finalOffer.sdp,
        type: finalOffer.type,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server rejected offer: ${response.status} ${errorText}`);
    }

    const answer = await response.json();

    await this.peerConnection.setRemoteDescription(
      new RTCSessionDescription({
        type: answer.type,
        sdp: answer.sdp,
      }),
    );
  }

  private waitForIceGathering(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.peerConnection) {
        reject(new Error('No peer connection'));
        return;
      }

      if (this.peerConnection.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        resolve();
      }, WebRTCCallService.ICE_GATHERING_TIMEOUT_MS);

      this.peerConnection.onicegatheringstatechange = () => {
        if (this.peerConnection?.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve();
        }
      };
    });
  }

  private handleIceCandidate(event: RTCPeerConnectionIceEvent): void {
    if (!event.candidate) return;

    this.pendingIceCandidates.push(event.candidate);

    if (this.iceCandidateSendTimer) {
      clearTimeout(this.iceCandidateSendTimer);
    }

    this.iceCandidateSendTimer = setTimeout(() => {
      this.sendBufferedIceCandidates();
    }, WebRTCCallService.ICE_CANDIDATE_BATCH_DELAY_MS);
  }

  private async sendBufferedIceCandidates(): Promise<void> {
    if (this.pendingIceCandidates.length === 0 || !this.sessionId) {
      return;
    }

    const candidates = [...this.pendingIceCandidates];
    this.pendingIceCandidates = [];

    for (const candidate of candidates) {
      try {
        await fetch(`${this.baseUrl}/webrtc/ice`, {
          method: 'POST',
          headers: this.authHeaders,
          body: JSON.stringify({
            session_id: this.sessionId,
            candidate: candidate.toJSON(),
          }),
        });
      } catch (error) {
        this.handleError('Failed to send ICE candidate', error);
      }
    }
  }

  private handleIceConnectionStateChange(): void {
    if (!this.peerConnection) return;

    switch (this.peerConnection.iceConnectionState) {
      case 'failed':
        this.handleError('ICE connection failed', null);
        break;
      case 'closed':
        this.setCallState(WebRTCCallState.DISCONNECTED);
        break;
    }
  }

  private handleConnectionStateChange(): void {
    if (!this.peerConnection) return;

    switch (this.peerConnection.connectionState) {
      case 'connected':
        this.setCallState(WebRTCCallState.CONNECTED);
        break;
      case 'failed':
        this.handleError('Peer connection failed', null);
        break;
      case 'closed':
        this.setCallState(WebRTCCallState.DISCONNECTED);
        break;
    }
  }

  private handleTrack(event: RTCTrackEvent): void {
    if (event.streams && event.streams[0]) {
      this.remoteStream = event.streams[0];
      this.remoteAudio.set(this.remoteStream);
      this.playRemoteAudio(this.remoteStream);
    }
  }

  private playRemoteAudio(stream: MediaStream): void {
    const audioElement = new Audio();
    audioElement.srcObject = stream;
    audioElement.autoplay = true;
    audioElement.play().catch((error) => {
      this.handleError('Failed to play remote audio', error);
    });
  }

  private async closeSession(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/webrtc/close`, {
        method: 'POST',
        headers: this.authHeaders,
        body: JSON.stringify({
          session_id: this.sessionId,
        }),
      });
    } catch (error) {
      this.handleError('Failed to close session on server', error);
    }
  }

  toggleMute(): boolean {
    if (!this.localStream) return false;

    const audioTrack = this.localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      return !audioTrack.enabled;
    }
    return false;
  }

  isMuted(): boolean {
    if (!this.localStream) return true;

    const audioTrack = this.localStream.getAudioTracks()[0];
    return audioTrack ? !audioTrack.enabled : true;
  }

  getCurrentState(): WebRTCCallState {
    return this.callState();
  }

  getDebugInfo(): WebRTCDebugInfo {
    return {
      callState: this.getCurrentState(),
      sessionId: this.sessionId,
      peerConnectionState: this.peerConnection?.connectionState,
      iceConnectionState: this.peerConnection?.iceConnectionState,
      iceGatheringState: this.peerConnection?.iceGatheringState,
      signalingState: this.peerConnection?.signalingState,
      hasLocalStream: !!this.localStream,
      hasRemoteStream: !!this.remoteStream,
      dataChannelState: this.audioDataChannel?.readyState,
      audioQueueLength: this.audioQueue.length,
      isPlayingAudio: this.isPlayingAudio,
      audioContextState: this.audioContext?.state,
      localTracks: this.localStream?.getTracks().map((t) => ({
        kind: t.kind,
        enabled: t.enabled,
        readyState: t.readyState,
      })),
      remoteTracks: this.remoteStream?.getTracks().map((t) => ({
        kind: t.kind,
        enabled: t.enabled,
        readyState: t.readyState,
      })),
      timestamp: Date.now(),
    };
  }

  private get authHeaders(): Record<string, string> {
    const sessionId = this.iamSessionId();
    if (!sessionId) {
      throw new Error('No active session — cannot authenticate WebRTC request');
    }
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`,
    };
  }

  private generateSessionId(): string {
    return `webrtc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setCallState(state: WebRTCCallState): void {
    this.callState.set(state);
  }

  private handleError(message: string, error?: unknown): void {
    console.error(`[WebRTC] ${message}`, error);
    this.cleanup();
    this.setCallState(WebRTCCallState.ERROR);
    this.error.set({ message, details: error });
  }

  private cleanup(): void {
    if (this.iceCandidateSendTimer) {
      clearTimeout(this.iceCandidateSendTimer);
      this.iceCandidateSendTimer = undefined;
    }
    this.pendingIceCandidates = [];

    if (this.audioDataChannel) {
      this.audioDataChannel.close();
      this.audioDataChannel = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.audioQueue = [];
    this.isPlayingAudio = false;
    this.nextPlayTime = 0;

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
    this.sessionId = null;
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
